import {
    CheckoutUpdateOptions,
    CheckoutUpdateParameters,
    Message,
    MessageChargeCreated,
    MessageClose,
    MessageError,
    MessageExecutePayment,
    MessageSubscriptionCreated,
    MessageSuccess,
    MessageThreeDsAuthorization,
    MessageThreeDsAuthorizationFailure,
    MessageThreeDsAuthorizationSuccess,
    MessageTokenCreated,
    MessageType,
    MessageValidationError,
} from "../../common/Messages";
import { CheckoutParams, CheckoutStyles } from "../../common/types";
import { development } from "../../common/utils/development";

import { Connector } from "./Connector";

type EventType =
    | "opened"
    | "closed"
    | "success"
    | "error"
    | "form-error"
    | "pending"
    | "token-created"
    | "charge-created"
    | "subscription-created"
    | "validation-error"
    | "three-ds-authorization"
    | "three-ds-authorization-success"
    | "three-ds-authorization-failure"
    | "three-ds-authorization-modal-opened";
type LegacyEventType = "callback";

export interface CheckoutConstructorParams extends CheckoutParams {
    appId: string;
}
export class Checkout {
    static create(params: CheckoutConstructorParams & Partial<CheckoutParams>): Checkout {
        return new Checkout(params);
    }

    static async submit(iframe: HTMLIFrameElement) {
        if (!iframe) {
            throw new Error("No element provided to 'submit'. Please provide the payment iframe element.");
        }

        const connector = Connector.getConnectorByIFrame(iframe);
        if (!connector) {
            throw new Error("No connector found for the element. Please provide the payment iframe element.");
        }

        return connector.submit();
    }

    static async update(
        iframe: HTMLIFrameElement,
        overrides: CheckoutUpdateParameters,
        options?: CheckoutUpdateOptions
    ) {
        if (!iframe) {
            throw new Error("No element provided to 'update'. Please provide the payment iframe element.");
        }

        const connector = Connector.getConnectorByIFrame(iframe);
        if (!connector) {
            throw new Error("No connector found for the element. Please provide the payment iframe element.");
        }

        return connector.update(overrides, options);
    }

    params: CheckoutConstructorParams;
    connector: Connector;

    private execute: Message<MessageExecutePayment>;

    constructor(params: CheckoutConstructorParams) {
        this.params = { ...params, hasBeforeClosingCallback: !!params.beforeClosing };
        this.connector = new Connector(this.params);

        this.connector.emitter.on("checkout:opened", this.handleOpened);
        this.connector.emitter.on("checkout:closed", this.handleClosed);
        this.connector.emitter.on("checkout:success", this.handleSuccess);
        this.connector.emitter.on("checkout:pending", this.handlePending);
        this.connector.emitter.on("checkout:error", this.handleError);
        this.connector.emitter.on("checkout:form-error", this.handleFormError);
        this.connector.emitter.on("checkout:token-created", this.handleTokenCreated);
        this.connector.emitter.on("checkout:charge-created", this.handleChargeCreated);
        this.connector.emitter.on("checkout:subscription-created", this.handleSubscriptionCreated);
        this.connector.emitter.on("checkout:validation-error", this.handleValidationError);

        this.connector.emitter.on("checkout:three-ds-authorization", this.handleThreeDsAuthorization);
        this.connector.emitter.on("checkout:three-ds-authorization-success", this.handleThreeDsAuthorizationSuccess);
        this.connector.emitter.on("checkout:three-ds-authorization-failure", this.handleThreeDsAuthorizationFailure);
        this.connector.emitter.on("checkout:three-ds-authorization-modal-opened", this.handleThreeDsModalOpened);
    }

    open = async (_params?: CheckoutParams & CheckoutStyles, parentNode?: HTMLElement) => {
        const params = { ...this.params, ..._params } as CheckoutParams & CheckoutStyles;
        this.execute = {
            data: { connectorId: undefined, params, appId: params.appId, checkout: params.checkout },
            type: MessageType.EXECUTE,
        };

        try {
            const checkout = await this.connector.open(this.execute, parentNode);
            development(() => console.info("6- Executing checkout..."));
            this.connector.emitter.emit("checkout:execute", this.execute);

            return checkout;
        } catch (error) {
            development(() => console.error(error));
        }
    };

    dispatchCheckoutEvent<PayloadType>(type: EventType | LegacyEventType, params?: PayloadType) {
        const legacyEvent = new CustomEvent(`gopay:${type}`, params);
        const openEvent = new CustomEvent(`univapay:${type}`, params);

        window.dispatchEvent(legacyEvent);
        window.dispatchEvent(openEvent);
    }

    handleOpened = (): void => {
        this.dispatchCheckoutEvent("opened");
        this.params.opened?.();
    };

    handleClosed = (message: MessageClose): void => {
        this.dispatchCheckoutEvent("closed");

        if (message?.failedSubscriptionId) {
            this.params.subscriptionId = message.failedSubscriptionId;
        }

        this.connector.close();
        this.params.closed?.();
    };

    handleSuccess = (message: MessageSuccess): void => {
        this.dispatchCheckoutEvent("success", { detail: message });
        this.dispatchCheckoutEvent("callback", { detail: message }); // Dispatch the `callback` event for backward compatibility
        this.params.onSuccess?.(message);
    };

    handlePending = (message: MessageSuccess): void => {
        this.dispatchCheckoutEvent("pending", { detail: message });
        this.params.onPending?.(message);
    };

    handleError = (message: MessageError): void => {
        this.dispatchCheckoutEvent("error", { detail: message });
        this.params.onError?.(message);
    };

    handleFormError = (message: MessageClose): void => {
        this.dispatchCheckoutEvent("form-error");

        if (message?.failedSubscriptionId) {
            this.params.subscriptionId = message.failedSubscriptionId;
        }

        this.connector.close();
        this.params.closed?.();
    };

    handleTokenCreated = (message: MessageTokenCreated): void => {
        this.dispatchCheckoutEvent("token-created", { detail: message });
        this.params.onTokenCreated?.(message);
    };

    handleChargeCreated = (message: MessageChargeCreated): void => {
        this.dispatchCheckoutEvent("charge-created", { detail: message });
        this.params.onChargeCreated?.(message);
    };

    handleSubscriptionCreated = (message: MessageSubscriptionCreated): void => {
        this.dispatchCheckoutEvent("subscription-created", { detail: message });
        this.params.onSubscriptionCreated?.(message);
    };

    handleThreeDsAuthorization = (message: MessageThreeDsAuthorization): void => {
        this.dispatchCheckoutEvent("three-ds-authorization", { detail: message });
        this.params.onThreeDsAuthorization?.(message);
    };

    handleThreeDsAuthorizationSuccess = (message: MessageThreeDsAuthorizationSuccess): void => {
        this.dispatchCheckoutEvent("three-ds-authorization-success", { detail: message });
        this.params.onThreeDsAuthorizationSuccess?.(message);
    };

    handleThreeDsAuthorizationFailure = (message: MessageThreeDsAuthorizationFailure): void => {
        this.dispatchCheckoutEvent("three-ds-authorization-failure", { detail: message });
        this.params.onThreeDsAuthorizationFailure?.(message);
    };

    handleThreeDsModalOpened = (message: MessageThreeDsAuthorizationFailure): void => {
        this.dispatchCheckoutEvent("three-ds-authorization-modal-opened", { detail: message });
        this.params.onThreeDsAuthorizationModalOpened?.();
    };

    handleValidationError = (message: MessageValidationError): void => {
        this.dispatchCheckoutEvent("validation-error", { detail: message });
        this.params.onValidationError?.(message);
    };
}
